/**
 * Client-side JWT payload decoder.
 * Does NOT verify signature — only reads claims for display/routing.
 */

/**
 * Status efetivo do estabelecimento, como a API o põe no token. Não é só o
 * status comercial: `deactivated` é a loja desativada pela Vapt Menu, que a
 * API manda no lugar do status comercial preservado pela desativação.
 */
export type SubscriptionStatus =
  | 'trial'
  | 'active'
  | 'past_due'
  | 'expired'
  | 'cancelled'
  | 'deactivated'
  | 'none';

export interface JwtClaims {
  sub: string;          // user id
  email: string;
  name: string;
  tenant_id?: string;
  role?: string;
  permissions?: string[];
  subscription_status?: SubscriptionStatus;
  trial_expires_at?: string; // ISO 8601
  exp: number;
  iat: number;
  [key: string]: unknown;
}

/**
 * Decode a JWT token and return its payload.
 * Returns null if the token is malformed.
 */
export function decodeJwt(token: string): JwtClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const json = JSON.parse(decoded);

    // Normalize ASP.NET claim types to short names
    return {
      sub:
        json.sub ||
        json['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ||
        '',
      email:
        json.email ||
        json['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
        '',
      name:
        json.full_name ||
        json.name ||
        json['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
        '',
      tenant_id: json.tenant_id || json.tenantId,
      role:
        json.role ||
        json['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
      permissions: normalizeArray(json.permissions || json.permission),
      subscription_status: json.subscription_status as SubscriptionStatus | undefined,
      trial_expires_at: json.trial_expires_at as string | undefined,
      exp: json.exp,
      iat: json.iat,
    };
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired (with optional buffer in seconds).
 */
export function isTokenExpired(token: string, bufferSeconds = 30): boolean {
  const claims = decodeJwt(token);
  if (!claims) return true;
  return Date.now() >= (claims.exp - bufferSeconds) * 1000;
}

function normalizeArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') return [value];
  return [];
}
